import React, { useEffect, useState, Component } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useForm } from 'react-hook-form';
import { CheckCircleIcon } from 'lucide-react';
const prompts = ['You have $1,000 and 10 days to make as much money as possible. What do you do? (Nothing illegal.)', 'A huge snowstorm will hit Florida tomorrow, but nobody else knows yet. How could you benefit from this information?', "What's a belief you hold that most friends would call wrong or crazy? Why?", 'What does everyone in your field believe that you think is wrong?', 'Name something overrated and something underrated. Explain both.', 'If you could place a sensor/camera anywhere to collect data, where and why? What could you learn?', 'What are you actually good at? (Anything counts.)', 'What rabbit hole did you recently go down? What did you learn?', 'Tell us something interesting.'];
interface FormData {
  name: string;
  email: string;
  major: string;
  year: string;
  response1: string;
  response2: string;
  response3: string;
  response4: string;
}
export function JoinUs() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  const [submitted, setSubmitted] = useState(false);
  const [selectedPrompts, setSelectedPrompts] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    watch,
    formState: {
      errors
    }
  } = useForm<FormData>();
  useEffect(() => {
    const shuffled = [...prompts].sort(() => Math.random() - 0.5);
    setSelectedPrompts(shuffled.slice(0, 4));
  }, []);
  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    // Format the email content
    const emailBody = `
New Application Submission
Name: ${data.name}
Email: ${data.email}
Major/Field: ${data.major}
Year: ${data.year}
Prompt 1: ${selectedPrompts[0]}
Response: ${data.response1}
Prompt 2: ${selectedPrompts[1]}
Response: ${data.response2}
Prompt 3: ${selectedPrompts[2]}
Response: ${data.response3}
Prompt 4: ${selectedPrompts[3]}
Response: ${data.response4}
    `;
    try {
      // Send email using mailto (opens user's email client)
      const mailtoLink = `mailto:officialtrungnguyen@gmail.com?subject=Pericles Capital Application - ${data.name}&body=${encodeURIComponent(emailBody)}`;
      window.location.href = mailtoLink;
      // Show success message after a short delay
      setTimeout(() => {
        setSubmitted(true);
        setIsSubmitting(false);
      }, 1000);
    } catch (error) {
      console.error('Error submitting application:', error);
      setIsSubmitting(false);
      alert('There was an error submitting your application. Please try again or email directly to officialtrungnguyen@gmail.com');
    }
  };
  const response1 = watch('response1') || '';
  const response2 = watch('response2') || '';
  const response3 = watch('response3') || '';
  const response4 = watch('response4') || '';
  if (submitted) {
    return <section id="join" className="w-full py-32 px-6 bg-navy-900" data-id="element-94">
        <div className="max-w-4xl mx-auto text-center" data-id="element-95">
          <motion.div initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} transition={{
          duration: 0.5,
          type: 'spring'
        }} data-id="element-96">
            <CheckCircleIcon className="w-24 h-24 text-[#14B8A6] mx-auto mb-6" data-id="element-97" />
          </motion.div>
          <h2 className="text-4xl font-bold mb-4" data-id="element-98">Application Received</h2>
          <p className="text-xl text-[#CBD5E1]" data-id="element-99">
            Thank you for your interest. We'll review your application and be in
            touch soon.
          </p>
        </div>
      </section>;
  }
  return <section id="join" ref={ref} className="w-full py-32 px-6 bg-navy-900" data-id="element-100">
      <div className="max-w-4xl mx-auto" data-id="element-101">
        <motion.h2 initial={{
        opacity: 0,
        y: 20
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="text-4xl md:text-5xl font-bold mb-12 text-center" data-id="element-102">
          Join Us
        </motion.h2>
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="mb-12 text-center" data-id="element-103">
          <p className="text-lg text-[#CBD5E1] mb-4" data-id="element-104">
            We recruit intentionally small cohorts (often 1–2 per discipline).
          </p>
          <p className="text-lg text-[#CBD5E1]" data-id="element-105">
            <span className="text-[#14B8A6] font-semibold" data-id="element-106">
              {'Builders > polish.'}
            </span>{' '}
            <span className="text-[#60A5FA] font-semibold" data-id="element-107">
              {'Curiosity > pedigree.'}
            </span>
          </p>
        </motion.div>
        <motion.form initial={{
        opacity: 0,
        y: 20
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.4
      }} onSubmit={handleSubmit(onSubmit)} className="bg-[#1F2937]/50 backdrop-blur-sm border border-[#CBD5E1]/10 rounded-lg p-8" data-id="element-108">
          <div className="grid md:grid-cols-2 gap-6 mb-6" data-id="element-109">
            <div data-id="element-110">
              <label className="block text-sm font-semibold mb-2" data-id="element-111">Name</label>
              <input {...register('name', {
              required: true
            })} className="w-full px-4 py-3 bg-[#0F172A] border border-[#CBD5E1]/20 rounded-lg focus:outline-none focus:border-[#60A5FA] transition-colors" placeholder="Your full name" data-id="element-112" />
            </div>
            <div data-id="element-113">
              <label className="block text-sm font-semibold mb-2" data-id="element-114">Email</label>
              <input {...register('email', {
              required: true,
              pattern: /^\S+@\S+$/i
            })} type="email" className="w-full px-4 py-3 bg-[#0F172A] border border-[#CBD5E1]/20 rounded-lg focus:outline-none focus:border-[#60A5FA] transition-colors" placeholder="your.email@northeastern.edu" data-id="element-115" />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-6 mb-8" data-id="element-116">
            <div data-id="element-117">
              <label className="block text-sm font-semibold mb-2" data-id="element-118">
                Major/Field
              </label>
              <input {...register('major', {
              required: true
            })} className="w-full px-4 py-3 bg-[#0F172A] border border-[#CBD5E1]/20 rounded-lg focus:outline-none focus:border-[#60A5FA] transition-colors" placeholder="e.g., Finance, Math, CS" data-id="element-119" />
            </div>
            <div data-id="element-120">
              <label className="block text-sm font-semibold mb-2" data-id="element-121">Year</label>
              <select {...register('year', {
              required: true
            })} className="w-full px-4 py-3 bg-[#0F172A] border border-[#CBD5E1]/20 rounded-lg focus:outline-none focus:border-[#60A5FA] transition-colors" data-id="element-122">
                <option value="" data-id="element-123">Select year</option>
                <option value="freshman" data-id="element-124">Freshman</option>
                <option value="sophomore" data-id="element-125">Sophomore</option>
                <option value="junior" data-id="element-126">Junior</option>
                <option value="senior" data-id="element-127">Senior</option>
                <option value="graduate" data-id="element-128">Graduate</option>
              </select>
            </div>
          </div>
          {selectedPrompts.map((prompt, index) => <div key={index} className="mb-8" data-id="element-129">
              <label className="block text-sm font-semibold mb-2 text-[#14B8A6]" data-id="element-130">
                Prompt {index + 1}
              </label>
              <p className="text-sm text-[#CBD5E1] mb-3" data-id="element-131">{prompt}</p>
              <textarea {...register(`response${index + 1}` as keyof FormData, {
            required: true,
            minLength: 50
          })} className="w-full px-4 py-3 bg-[#0F172A] border border-[#CBD5E1]/20 rounded-lg focus:outline-none focus:border-[#60A5FA] transition-colors min-h-32 resize-y" placeholder="Your response..." data-id="element-132" />
              <p className="text-xs text-[#CBD5E1] mt-1" data-id="element-133">
                {index === 0 && response1.length}
                {index === 1 && response2.length}
                {index === 2 && response3.length}
                {index === 3 && response4.length} characters (minimum 50)
              </p>
            </div>)}
          <button type="submit" disabled={isSubmitting} className="w-full py-4 bg-[#60A5FA] text-white rounded-lg font-semibold text-lg hover:bg-[#3B82F6] transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-134">
            {isSubmitting ? 'Submitting...' : 'Submit Application'}
          </button>
        </motion.form>
      </div>
    </section>;
}